import { InputBaseComponentProps, TextField, TextFieldProps } from "@mui/material";
import { ElementType } from "react";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";

import { NumberFormatCustom } from "../../components/format";

export type FormNumberValue = number | string | null;

export type FormNumberProps<TFormValues extends FieldValues> = Omit<TextFieldProps, "name" | "defaultValue"> & {
  name: Path<TFormValues>;
  format?: "dollar" | "percent" | "whole" | "none";
  stringify?: boolean;
};

function getNumberType<TFormValues extends FieldValues>(format: FormNumberProps<TFormValues>["format"]) {
  return {
    dollar: {
      allowNegative: false,
      thousandSeparator: true,
      decimalScale: 4,
      prefix: "$",
      suffix: "",
    },
    percent: {
      allowNegative: false,
      thousandSeparator: true,
      decimalScale: 2,
      prefix: "",
      suffix: "%",
    },
    whole: {
      allowNegative: false,
      thousandSeparator: false,
      decimalScale: 0,
      prefix: "",
      suffix: "",
    },
    none: {
      allowNegative: false,
      thousandSeparator: true,
      decimalScale: 2,
      prefix: "",
      suffix: "",
    },
  }[format ?? "none"];
}

export function FormNumber<TFormValues extends FieldValues>({
  name,
  format = "none",
  stringify,
  slotProps,
  ...rest
}: FormNumberProps<TFormValues>) {
  const { control } = useFormContext<TFormValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          {...rest}
          autoComplete="off"
          slotProps={{
            ...slotProps,
            input: {
              inputComponent: NumberFormatCustom as unknown as ElementType<InputBaseComponentProps>,
              inputProps: {
                ...getNumberType<TFormValues>(format),
                stringify,
              },
            },
          }}
        />
      )}
    />
  );
}
